import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { toast } from 'react-hot-toast';
import Select from 'react-select';
import { prescriptionAPI, icdAPI } from '../services/api';
import { 
  Camera, 
  Upload, 
  ArrowLeft, 
  X, 
  Check,
  FileText,
  User
} from 'lucide-react';
import CameraCapture from './CameraCapture';

const PrescriptionUpload = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  
  const [loading, setLoading] = useState(false);
  const [icdCodes, setIcdCodes] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [showCamera, setShowCamera] = useState(false);
  const [capturedImage, setCapturedImage] = useState(null);
  const [selectedIcdCodes, setSelectedIcdCodes] = useState([]);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm();

  useEffect(() => {
    fetchIcdCodes();
  }, []);

  const fetchIcdCodes = async () => {
    try {
      const response = await icdAPI.getAll();
      const options = response.data.map(code => ({
        value: code.code,
        label: `${code.code} - ${code.description}`,
        description: code.description
      }));
      setIcdCodes(options);
    } catch (error) {
      toast.error('Failed to fetch ICD codes');
    }
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      validateAndSetFile(file);
    }
  };

  const validateAndSetFile = (file) => {
    const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg', 'application/pdf'];
    const maxSize = 10 * 1024 * 1024; // 10MB

    if (!allowedTypes.includes(file.type)) {
      toast.error('Please select a valid file type (JPEG, PNG, or PDF)');
      return;
    }

    if (file.size > maxSize) {
      toast.error('File size must be less than 10MB');
      return;
    }

    setSelectedFile(file);
    setCapturedImage(null);
  };

  const handlePhotoCapture = (imageSrc) => {
    setCapturedImage(imageSrc);
    setShowCamera(false);
    
    // Convert base64 to file
    fetch(imageSrc)
      .then(res => res.blob())
      .then(blob => {
        const file = new File([blob], `prescription_${Date.now()}.jpg`, { type: 'image/jpeg' });
        setSelectedFile(file);
      })
      .catch(error => {
        console.error('Error converting image to file:', error);
        toast.error('Failed to process captured image');
      });
  };

  const handleIcdCodeChange = (selectedOptions) => {
    setSelectedIcdCodes(selectedOptions || []);
  };

  const onSubmit = async (data) => {
    if (!selectedFile) {
      toast.error('Please select a file or capture a photo');
      return;
    }

    if (selectedIcdCodes.length === 0) {
      toast.error('Please select at least one ICD code');
      return;
    }

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('patient_name', data.patient_name);
      formData.append('icd_codes', JSON.stringify(selectedIcdCodes.map(code => code.value)));
      formData.append('file', selectedFile);

      await prescriptionAPI.upload(formData);
      toast.success('Prescription uploaded successfully');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload prescription');
    } finally {
      setLoading(false);
    }
  };

  const removeFile = () => {
    setSelectedFile(null);
    setCapturedImage(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-teal-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Enhanced Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/dashboard')}
            className="flex items-center text-blue-600 hover:text-blue-700 transition-colors mb-6 group"
          >
            <ArrowLeft className="h-5 w-5 mr-2 group-hover:-translate-x-1 transition-transform" />
            Back to Dashboard
          </button>
          
          <div className="text-center mb-8">
            <h1 className="text-4xl font-bold text-gray-800 mb-3">Upload Prescription</h1>
          </div>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
          {/* Patient Information Section */}
          <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
            <h3 className="text-lg font-semibold text-gray-800 mb-6 pb-3 border-b border-gray-100">
              Patient Information
            </h3>
            
            <div className="space-y-6">
              {/* Patient Name */}
              <div>
                <label htmlFor="patient_name" className="form-label">
                  Patient Name
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <User className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    id="patient_name"
                    type="text"
                    className={`form-input form-input-with-icon ${
                      errors.patient_name ? 'border-red-500 focus:ring-red-500 focus:border-red-500' : ''
                    }`}
                    placeholder="Enter patient name"
                    {...register('patient_name', {
                      required: 'Patient name is required',
                      minLength: {
                        value: 2,
                        message: 'Patient name must be at least 2 characters',
                      },
                    })}
                  />
                </div>
                {errors.patient_name && (
                  <p className="mt-2 text-sm text-red-600 flex items-center">
                    <span className="w-1.5 h-1.5 bg-red-500 rounded-full mr-2"></span>
                    {errors.patient_name.message}
                  </p>
                )}
              </div>

              {/* ICD Codes */}
              <div>
                <label className="form-label">
                  ICD Codes
                </label>
                <Select
                  isMulti
                  options={icdCodes}
                  value={selectedIcdCodes}
                  onChange={handleIcdCodeChange}
                  placeholder="Select ICD codes..."
                  className="react-select-container"
                  classNamePrefix="react-select"
                />
                <p className="mt-2 text-sm text-gray-500">
                  Select one or more ICD codes for this prescription
                </p>
              </div>
            </div>
          </div>

          {/* Enhanced File Upload Section */}
          <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
            <label className="form-label">Prescription File</label>
            
            {!selectedFile ? (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Enhanced Camera Capture */}
                <button
                  type="button"
                  onClick={() => setShowCamera(true)}
                  className="border-2 border-dashed border-gray-300 rounded-xl p-6 text-center hover:border-blue-400 transition-colors group w-full cursor-pointer"
                >
                  <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-blue-600 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
                    <Camera className="h-8 w-8 text-white" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    Capture Photo
                  </h3>
                  <p className="text-gray-600 mb-4">
                    Use your camera to capture the prescription
                  </p>
                  <div className="text-blue-600 text-sm font-medium group-hover:text-blue-700">
                    Click to open camera →
                  </div>
                </button>

                {/* Enhanced File Upload */}
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="border-2 border-dashed border-gray-300 rounded-xl p-6 text-center hover:border-teal-400 transition-colors group w-full cursor-pointer"
                >
                  <div className="w-16 h-16 bg-gradient-to-r from-teal-500 to-teal-600 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform">
                    <Upload className="h-8 w-8 text-white" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    Upload File
                  </h3>
                  <p className="text-gray-600 mb-4">
                    Or upload an image or PDF file
                  </p>
                  <div className="text-teal-600 text-sm font-medium group-hover:text-teal-700">
                    Click to choose file →
                  </div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*,.pdf"
                    onChange={handleFileSelect}
                    className="hidden"
                  />
                </button>
              </div>
            ) : (
              <div className="border-2 border-gray-200 rounded-xl p-6 bg-gray-50">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-teal-500 rounded-full flex items-center justify-center">
                      <FileText className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <p className="text-lg font-semibold text-gray-900">
                        {selectedFile.name}
                      </p>
                      <p className="text-sm text-gray-600">
                        {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={removeFile}
                    className="p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-full transition-colors"
                    title="Remove file"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>
                
                {capturedImage && (
                  <div className="mt-4">
                    <img
                      src={capturedImage}
                      alt="Captured prescription"
                      className="max-w-full h-auto rounded-lg border-2 border-gray-200 shadow-sm"
                    />
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Enhanced Submit Buttons */}
          <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
            <div className="flex flex-col sm:flex-row gap-4">
              <button
                type="button"
                onClick={() => navigate('/dashboard') && setShowCamera(false)}
                className="btn-secondary flex-1 flex items-center justify-center py-4"
              >
                <X className="h-5 w-5 mr-2" />
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="btn-primary flex-1 flex items-center justify-center py-4"
              >
                {loading ? (
                  <div className="spinner mr-3"></div>
                ) : (
                  <Check className="h-5 w-5 mr-3" />
                )}
                {loading ? 'Uploading...' : 'Upload Prescription'}
              </button>
            </div>
          </div>
        </form>

        {/* Camera Modal using CameraCapture component */}
        {showCamera && (
          <CameraCapture
            onCapture={handlePhotoCapture}
            onClose={() => setShowCamera(false)}
            title="Capture Prescription"
            defaultFacingMode="environment" // Back camera by default
            showCameraSelection={true}
          />
        )}
      </div>
    </div>
  );
};

export default PrescriptionUpload; 